import { z } from "zod";

import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import { getStorySequenceFeedbackCue } from "@/audio/curriculum-catalog/chinese-feedback-cues";
import type { StorySequencePayload } from "@/content/chinese";
import {
  boundedTextSchema,
  localResourceReferenceSchema
} from "@/domain/schemas";
import type { ActiveHintStage } from "@/game/feedback";
import type { InputMethod } from "@/game/input";
import {
  createSequenceState,
  getSequenceHint,
  isSequenceComplete,
  reduceSequence,
  sequenceItemSchema,
  sequencePayloadSchema,
  type SequenceMoveDirection,
  type SequenceState
} from "@/game/templates/sequence";

const storySequencePayloadSchema = z.strictObject({
  narration: boundedTextSchema(480),
  narrationAudio: localResourceReferenceSchema,
  items: z.array(sequenceItemSchema).min(2).max(8),
  correctOrder: z.array(z.string()).min(2).max(8),
  hint: boundedTextSchema(240)
});

export function parseStorySequencePayload(
  value: unknown
): StorySequencePayload {
  const payload = storySequencePayloadSchema.parse(value);
  sequencePayloadSchema.parse({
    items: payload.items,
    correctOrder: payload.correctOrder
  });
  return payload;
}

export interface StorySequenceActivityState {
  readonly activity: SequenceState;
  readonly feedback: "retry" | null;
  readonly narration: string;
  readonly narrationAudio: string;
  readonly submitted: boolean;
}

export type StorySequenceAction =
  | {
      readonly type: "focus-item";
      readonly itemId: string;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "move-item";
      readonly itemId: string;
      readonly direction: SequenceMoveDirection;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "check-order";
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "request-hint" }
  | { readonly type: "replay" };

export interface StorySequenceItemRenderModel {
  readonly canMoveBackward: boolean;
  readonly canMoveForward: boolean;
  readonly focused: boolean;
  readonly hinted: boolean;
  readonly id: string;
  readonly label: string;
  readonly position: number;
}

export interface StorySequenceFeedbackRenderModel {
  readonly audioPath: string;
  readonly kind: "hint" | "retry";
  readonly message: string;
  readonly playbackRequest: number;
  readonly stage: ActiveHintStage;
}

export interface StorySequenceRenderModel {
  readonly feedback: StorySequenceFeedbackRenderModel | null;
  readonly focusedItemId: string | null;
  readonly items: readonly StorySequenceItemRenderModel[];
  readonly narration: string;
  readonly narrationAudio: string;
}

function toTemplatePayload(payload: StorySequencePayload) {
  return {
    items: payload.items,
    correctOrder: payload.correctOrder
  };
}

function createStorySequenceState(
  payload: StorySequencePayload
): StorySequenceActivityState {
  return {
    activity: createSequenceState(toTemplatePayload(payload)),
    feedback: null,
    narration: payload.narration,
    narrationAudio: payload.narrationAudio,
    submitted: false
  };
}

function reduceStorySequence(
  state: StorySequenceActivityState,
  payload: StorySequencePayload,
  action: StorySequenceAction
): StorySequenceActivityState {
  switch (action.type) {
    case "focus-item":
      return {
        ...state,
        activity: reduceSequence(state.activity, action),
        feedback: null
      };
    case "move-item":
      return {
        ...state,
        activity: reduceSequence(state.activity, action),
        feedback: null,
        submitted: false
      };
    case "check-order": {
      if (isSequenceComplete(state.activity)) {
        return {
          ...state,
          activity: {
            ...state.activity,
            lastInputMethod: action.inputMethod
          },
          feedback: null,
          submitted: true
        };
      }

      const retriedActivity = reduceSequence(state.activity, {
        type: "retry"
      });
      const activityWithVisibleHint =
        state.activity.hintState.stage === "none"
          ? reduceSequence(retriedActivity, {
              type: "request-hint"
            })
          : retriedActivity;

      return {
        ...state,
        activity: {
          ...activityWithVisibleHint,
          lastInputMethod: action.inputMethod
        },
        feedback: "retry",
        submitted: false
      };
    }
    case "request-hint":
      return {
        ...state,
        activity: reduceSequence(state.activity, {
          type: "request-hint"
        }),
        feedback: null
      };
    case "replay":
      return createStorySequenceState(payload);
  }
}

function getStorySequenceMetrics(
  state: StorySequenceActivityState
): ActivityCompletionMetrics {
  const completed =
    state.submitted && isSequenceComplete(state.activity);
  const totalChecks = state.activity.retryCount + (completed ? 1 : 0);
  const correctRate =
    completed && totalChecks > 0 ? 1 / totalChecks : 0;

  return {
    completed,
    score: Math.round(correctRate * 100),
    correctRate,
    hintCount: state.activity.hintState.hintCount,
    retryCount: state.activity.retryCount
  };
}

function getFeedback(
  state: StorySequenceActivityState,
  payload: StorySequencePayload
): StorySequenceFeedbackRenderModel | null {
  if (state.feedback === "retry") {
    const hint = getSequenceHint(state.activity);

    if (hint === null) {
      return null;
    }

    const cue = getStorySequenceFeedbackCue(payload, "retry", hint);

    return {
      audioPath: cue.audioPath,
      kind: "retry",
      message: cue.text,
      playbackRequest:
        state.activity.hintState.hintCount + state.activity.retryCount,
      stage: state.activity.hintState.stage === "none"
        ? "gentle-hint"
        : state.activity.hintState.stage
    };
  }

  const hint = getSequenceHint(state.activity);
  if (hint === null) {
    return null;
  }

  const cue = getStorySequenceFeedbackCue(payload, "hint", hint);

  return {
    audioPath: cue.audioPath,
    kind: "hint",
    message: cue.text,
    playbackRequest:
      state.activity.hintState.hintCount + state.activity.retryCount,
    stage: hint.stage
  };
}

function isHintedItem(
  state: StorySequenceActivityState,
  itemId: string,
  index: number
): boolean {
  const hint = getSequenceHint(state.activity);

  if (hint === null) {
    return false;
  }

  switch (hint.stage) {
    case "gentle-hint":
      return hint.targetIndex === index;
    case "reduced-choices":
      return hint.candidateItemIds.includes(itemId);
    case "replay-demonstration":
      return true;
  }
}

function toStorySequenceRenderModel(
  state: StorySequenceActivityState,
  payload: StorySequencePayload
): StorySequenceRenderModel {
  const lastIndex = state.activity.order.length - 1;
  const items = state.activity.order.flatMap((itemId, index) => {
    const item = payload.items.find(({ id }) => id === itemId);

    return item === undefined
      ? []
      : [
          {
            canMoveBackward: index > 0,
            canMoveForward: index < lastIndex,
            focused: state.activity.focusedItemId === itemId,
            hinted: isHintedItem(state, itemId, index),
            id: itemId,
            label: item.label,
            position: index + 1
          }
        ];
  });

  return {
    feedback: getFeedback(state, payload),
    focusedItemId: state.activity.focusedItemId,
    items,
    narration: state.narration,
    narrationAudio: state.narrationAudio
  };
}

export const storySequenceAdapter: ActivityAdapter<
  StorySequencePayload,
  StorySequenceActivityState,
  StorySequenceAction,
  StorySequenceRenderModel
> = {
  activityType: "story-sequence",
  parsePayload: parseStorySequencePayload,
  createInitialState: (payload, context) => {
    void context;
    return createStorySequenceState(payload);
  },
  reduce: (state, payload, action, context) => {
    void context;
    return reduceStorySequence(state, payload, action);
  },
  getMetrics: (state, payload) => {
    void payload;
    return getStorySequenceMetrics(state);
  },
  toRenderModel: toStorySequenceRenderModel
};

export const storySequenceHostActions = {
  requestHint: () => ({ type: "request-hint" }) as const,
  replay: () => ({ type: "replay" }) as const
};
